import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskLine, FadeIn } from "../components/Reveal";
import ClosingBlock from "../components/ClosingBlock";
import { CLINIC } from "../data/catalog";

const BELIEFS = [
  {
    title: "Most Ayurvedic products have never met an Ayurvedic doctor.",
    body: "That sentence is the reason Ayushi exists. The market is full of Ayurvedic labels with no Ayurvedic physician behind them.",
  },
  {
    title: "A cosmetic should not pretend to be a medicine.",
    body: "Our products cleanse, soften and condition. If you are dealing with a condition, that is a consultation — not a jar.",
  },
  {
    title: "Ten is enough.",
    body: "We make what we can make properly, in our own facility, in small batches. When ten feels stretched, we will stop adding.",
  },
  {
    title: "Natural is not the same as harmless.",
    body: "Natural ingredients can still cause reactions. Patch test first — we say so on every product page.",
  },
  {
    title: "Her name is on it.",
    body: "If something is wrong, there is a person to say it to. That person sees patients six days a week.",
  },
];

const StoryPage = () => (
  <div className="pt-36 md:pt-44" data-testid="story-page">
    <header className="mx-auto max-w-7xl px-4 md:px-8">
      <FadeIn>
        <div className="flex items-center gap-4">
          <span className="h-px w-12 bg-ink/30" />
          <p className="eyebrow">Our story</p>
        </div>
      </FadeIn>
      <h1 className="mt-8 font-serif font-light leading-[1.05] text-[11vw] sm:text-6xl lg:text-7xl">
        <MaskLine delay={0.15}>A clinic that started</MaskLine>
        <MaskLine delay={0.28}>
          <span className="italic text-sage-deep">making things.</span>
        </MaskLine>
      </h1>
    </header>

    <section className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-20">
      <div className="grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <div className="space-y-7 text-lg leading-relaxed text-ink/75">
            <p>
              Ayushi did not begin as a brand. It began as a clinic — Ayushi Ayurveda Wellness, at
              Chavadimukku, Sreekaryam, where Dr. Shibina Shemim has been seeing patients for
              years. Sixty-nine of them have written reviews on Google. The average is 4.9.
            </p>
            <p>
              The products came from the patients. People kept asking for something to use at home,
              and what was on the shelves was not what she would have made herself. So she started
              making it — under her own licence, in small quantities, the way she would prepare
              anything for someone sitting across from her desk.
            </p>
            <p>
              All ten products in the range exist because someone asked. Nothing was added to fill
              a shelf.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9">
          <div className="overflow-hidden">
            <img src="/assets/range-hero.png" alt="The Ayushi range" className="w-full object-cover" loading="lazy" />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="border-t hairline bg-white/50" data-testid="beliefs-section">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28">
        <Reveal>
          <p className="eyebrow">What we believe</p>
        </Reveal>
        <div className="mt-10">
          {BELIEFS.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.06}>
              <div className="grid gap-4 border-t hairline py-8 md:grid-cols-12" data-testid={`belief-${i}`}>
                <p className="font-serif text-2xl italic text-sage-deep md:col-span-2">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-serif text-2xl font-light leading-snug md:col-span-5">{b.title}</h3>
                <p className="text-[15px] leading-relaxed text-ink/65 md:col-span-5">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28" data-testid="about-doctor">
      <div className="grid items-center gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <div className="overflow-hidden rounded-t-[999px]">
            <img src="/assets/img-b3.jpeg" alt="Dr. Shibina Shemim" className="aspect-[4/5] w-full object-cover" data-testid="doctor-photo" />
          </div>
        </Reveal>
        <div className="lg:col-span-7 lg:col-start-6">
          <Reveal>
            <p className="eyebrow">About Dr. Shibina</p>
            <h2 className="mt-4 font-serif text-4xl font-light leading-tight md:text-5xl">
              Dr. Shibina Shemim, <span className="italic text-sage-deep">BAMS, PGDAC.</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/70">
              Ayurveda consultant and founder of Ayushi Ayurveda Wellness, Chavadimukku,
              Sreekaryam. Member, Ayurveda Medical Association. She sees patients Monday to
              Saturday — the formulation work happens around consultations, which is why it took
              years to reach ten products.
            </p>
            <blockquote className="mt-10 border-l-2 border-sage-deep/50 pl-6 font-serif text-2xl font-light italic leading-relaxed text-ink/85 md:text-3xl">
              "What I love most is preparing the products by hand."
            </blockquote>
            <Link to="/the-clinic" data-testid="story-clinic-cta" className="group mt-10 inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 text-[12px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-ink-deep">
              Visit the Clinic
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>

    <ClosingBlock />
  </div>
);

export default StoryPage;
